import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { getArticlesBySection } from "@/data/articles";
import { articleImageAlts } from "@/data/articleImages";
import {
  inferPredstavitevTag,
  PREDSTAVITEV_TAG_ORDER,
  type PredstavitevTag,
} from "@/lib/articleTags";

const TECH_SLUGS = new Set([
  "registracija-najem-spletne-trgovine",
  "prijava-urejanje-trgovine",
  "kaj-vse-lahko-urejam-v-nadzorni-plosci",
  "kaj-vse-lahko-spremljam-v-nadzorni-plosci",
  "dodajanje-izdelkov",
]);

export const Route = createFileRoute("/predstavitev-prodajalca")({
  head: () => ({
    meta: [
      { title: "Predstavitev ponudnika — vodnik za prodajalce | kupish.si" },
      {
        name: "description",
        content:
          "Nasveti in vodniki za ponudnike na kupish.si: kako pripraviti izdelke, fotografije, opise, cene in promocijo trgovine.",
      },
      { property: "og:title", content: "Predstavitev ponudnika — vodnik za prodajalce" },
      {
        property: "og:description",
        content: "Podpora in nasveti za ponudnike pri spletni predstavitvi izdelkov in trgovine.",
      },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://kupish.si/predstavitev-prodajalca",
      },
    ],
  }),
  loader: async () => {
    const { listPublishedDrafts } = await import("@/lib/article-autowrite.functions");
    const drafts = await listPublishedDrafts().catch(() => []);
    return { drafts };
  },
  component: Page,
});

function Page() {
  const { drafts } = Route.useLoaderData();
  const all = useMemo(() => {
    const fromDb = (drafts ?? [])
      .filter((d) => (d.section ?? "unikatne-vsebine") === "unikatne-vsebine")
      .map((d) => ({
        slug: d.slug,
        title: d.title,
        section: "unikatne-vsebine" as const,
        date: d.published_at ?? "",
        excerpt: d.excerpt ?? "",
        content: d.content ?? "",
        featuredImage: d.cover_url ?? undefined,
      }));
    return [...fromDb, ...getArticlesBySection("unikatne-vsebine")]
      .filter((a) => !TECH_SLUGS.has(a.slug))
      .map((a) => ({ ...a, tag: inferPredstavitevTag(a) }));
  }, [drafts]);

  const counts = useMemo(() => {
    const c = new Map<PredstavitevTag, number>();
    for (const a of all) c.set(a.tag, (c.get(a.tag) ?? 0) + 1);
    return c;
  }, [all]);

  const [active, setActive] = useState<PredstavitevTag | null>(null);
  const visible = active ? all.filter((a) => a.tag === active) : all;

  return (
    <section className="mx-auto max-w-5xl px-6 py-24">
      <div className="eyebrow">Za prodajalce</div>
      <h1 className="font-serif text-5xl md:text-6xl mt-3 leading-tight">
        Predstavitev <span className=" text-accent">ponudnika</span>
      </h1>
      <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
        Podpora in nasveti za ponudnike pri kvalitetni spletni predstavitvi izdelkov in trgovine —
        od priprave izdelka in fotografij do opisov, SEO in oglaševanja.
      </p>

      <div className="mt-10 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setActive(null)}
          className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-full border transition-colors ${
            active === null
              ? "bg-accent text-accent-foreground border-accent"
              : "border-border hover:border-accent hover:text-accent"
          }`}
        >
          Vsi vodniki ({all.length})
        </button>
        {PREDSTAVITEV_TAG_ORDER.filter((t) => (counts.get(t) ?? 0) > 0).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setActive(t === active ? null : t)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-full border transition-colors ${
              active === t
                ? "bg-accent text-accent-foreground border-accent"
                : "border-border hover:border-accent hover:text-accent"
            }`}
          >
            {t} ({counts.get(t)})
          </button>
        ))}
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-8">
        {visible.map((a) => (
          <Link
            key={a.slug}
            to="/clanek/$slug"
            params={{ slug: a.slug }}
            className="group block border border-border rounded-lg overflow-hidden hover:border-accent transition-colors bg-card"
          >
            {a.featuredImage && (
              <div className="aspect-[16/10] overflow-hidden bg-muted">
                <img
                  src={a.featuredImage}
                  alt={articleImageAlts[a.slug] ?? a.title}
                  title={a.title}
                  width={640}
                  height={400}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                />
              </div>
            )}
            <div className="p-6">
              <h2 className="font-serif text-2xl leading-snug group-hover:text-accent text-foreground min-h-[3.4rem]">
                {a.title}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{a.excerpt}</p>
              <span className="mt-3 inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-border text-muted-foreground">
                {a.tag}
              </span>
              <span className="mt-4 block text-xs uppercase tracking-wider text-accent">
                Preberi →
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
